<script>
  // The Update Feed page. Reverse-chronological, day-bucketed, numbered-page
  // feed of Binder Updates scoped to a group (/groups/{id}/updates) or a binder
  // (/{short}/updates). When the route resolves an Update's per-binder seq
  // (/{short}/updates/{seq}), renders the shareable detail view instead. There
  // is no generic "my feed" — a scope is required.
  import { route, navigate } from '../../lib/stores/router.svelte.js';
  import { isUserAnonymous, initiateDiscordOAuth } from '../../lib/stores/auth.svelte.js';
  import { updatesState, setScope, loadFeed, goToPage } from '../../lib/stores/updates.svelte.js';
  import { bucketByDay } from '../../lib/logic/updates-logic.js';
  import UpdateBlock from './UpdateBlock.svelte';
  import UpdateDetail from './UpdateDetail.svelte';
  import { untrack } from 'svelte';
  import { activeSubgroupIds, syncSelectionFromRoute } from '../../lib/stores/browse.svelte.js';

  // The group view's subgroup selection: the picker's stored selection for
  // this server, like every other group-scoped page (ADR-0021 decision 7).
  // Memoized in the store, so an unchanged selection keeps its reference and
  // does not refetch.
  const subgroupIds = $derived(route.params?.group ? activeSubgroupIds() : []);

  // Apply the route's scope params and the selection, then load. Re-runs when
  // either changes; the store writes and the load are untracked so the feed's
  // own state never re-fires this effect.
  $effect(() => {
    const p = route.params || {};
    if (p.seq) return; // detail view handles its own load
    // No generic "my feed" - a group or binder scope is required.
    if (!p.group && !p.binder) {
      navigate('home', {}, true);
      return;
    }
    // Point the browse selection at this feed's server before reading it.
    if (p.group) untrack(() => syncSelectionFromRoute({ group: p.group }));
    const ids = subgroupIds;
    untrack(() => {
      const scope = p.group ? 'group' : 'binder';
      setScope({ scope, groupId: p.group || null, binderUrl: p.binder || null, subgroupIds: ids });
      if (p.page) updatesState.page = Number(p.page) || 1;
      loadFeed();
    });
  });

  const buckets = $derived(bucketByDay(updatesState.updates));

  function formatDay(dayKey) {
    if (dayKey === 'unknown') return 'Earlier';
    const d = new Date(dayKey + 'T00:00:00');
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    const isToday = d.toDateString() === today.toDateString();
    const isYesterday = d.toDateString() === yesterday.toDateString();

    if (isToday) return 'Today';
    if (isYesterday) return 'Yesterday';

    return d.toLocaleDateString(undefined, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    });
  }

  function scopeTitle() {
    if (updatesState.scope === 'group') return 'Group Updates';
    return updatesState.updates[0]?.binder_name || 'Binder Updates';
  }

  function scopeSubtitle() {
    if (updatesState.scope === 'group') return "Recent changes across this group's binders";
    const owner = updatesState.updates[0]?.owner_name;
    return owner ? `Recent changes to ${owner}'s binder` : 'Recent changes to this binder';
  }
</script>

{#if route.params?.seq}
  <UpdateDetail binderUrl={route.params.binder} seq={route.params.seq} />
{:else}
  <main class="feed-page">
    <div class="feed-container">
      <header class="feed-header">
        <div class="feed-title-row">
          <h1 class="feed-title">{scopeTitle()}</h1>
          {#if updatesState.total > 0}
            <span class="feed-summary">{updatesState.total} updates</span>
          {/if}
        </div>
        <p class="feed-subtitle">{scopeSubtitle()}</p>
      </header>

      {#if updatesState.loading}
        <div class="feed-loading">
          <div class="loading-shimmer"></div>
          <div class="loading-shimmer loading-shimmer--short"></div>
          <div class="loading-shimmer"></div>
        </div>
      {:else if updatesState.error}
        <div class="feed-state-card feed-error-card">
          <span class="state-icon">&#9888;</span>
          <p class="state-message">{updatesState.error}</p>
        </div>
      {:else if updatesState.updates.length === 0}
        <div class="feed-state-card feed-empty-card">
          <span class="state-icon">&#128214;</span>
          <p class="state-message">No updates yet.</p>
          {#if isUserAnonymous()}
            <p class="state-hint">Claim a binder or join a group to start seeing changes here.</p>
            <button class="cta-btn" onclick={() => initiateDiscordOAuth()}>
              Sign in with Discord
            </button>
          {:else}
            <p class="state-hint">
              Claim a binder or join a group, and updates will appear after each sync.
            </p>
            <button class="cta-btn" onclick={() => navigate('binders')}> Browse binders </button>
          {/if}
        </div>
      {:else}
        {#each buckets as bucket (bucket.day)}
          <section class="day-section">
            <div class="day-header">
              <span class="day-label">{formatDay(bucket.day)}</span>
              <span class="day-count">{bucket.updates.length}</span>
            </div>
            {#each bucket.updates as update (update.id)}
              <UpdateBlock {update} />
            {/each}
          </section>
        {/each}

        {#if updatesState.totalPages > 1}
          <nav class="pager" aria-label="Feed pages">
            <button
              class="pager-btn pager-btn--prev"
              disabled={updatesState.page <= 1}
              onclick={() => goToPage(updatesState.page - 1)}
              aria-label="Newer updates"
            >
              <span class="pager-arrow">&#8592;</span> Newer
            </button>
            <span class="pager-status">
              <span class="pager-current">{updatesState.page}</span>
              <span class="pager-sep">/</span>
              <span class="pager-total">{updatesState.totalPages}</span>
            </span>
            <button
              class="pager-btn pager-btn--next"
              disabled={updatesState.page >= updatesState.totalPages}
              onclick={() => goToPage(updatesState.page + 1)}
              aria-label="Older updates"
            >
              Older <span class="pager-arrow">&#8594;</span>
            </button>
          </nav>
        {/if}
      {/if}
    </div>
  </main>
{/if}

<style>
  /* ── Page shell ── */
  .feed-page {
    width: 100%;
    min-height: 100vh;
    display: flex;
    justify-content: center;
    padding: var(--space-lg, 24px) var(--space-md, 16px) var(--space-xl, 32px);
    box-sizing: border-box;
  }

  .feed-container {
    width: 100%;
    max-width: 660px;
  }

  /* ── Page header ── */
  .feed-header {
    margin-bottom: var(--space-xl, 32px);
    padding-bottom: var(--space-md, 16px);
    border-bottom: 1px solid var(--color-border, #e6e0d5);
  }

  .feed-title-row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: var(--space-sm, 8px);
    margin-bottom: 4px;
  }

  .feed-title {
    font-family: var(--font-serif, Georgia, serif);
    font-size: 1.75rem;
    font-weight: 700;
    letter-spacing: -0.02em;
    color: var(--color-text, #2c2419);
    margin: 0;
  }

  .feed-summary {
    font-size: 0.8rem;
    font-weight: 500;
    color: var(--color-text-tertiary, #9a8e7c);
    white-space: nowrap;
    letter-spacing: 0.02em;
    text-transform: uppercase;
  }

  .feed-subtitle {
    margin: 0;
    font-size: 0.9rem;
    color: var(--color-text-secondary, #5a5040);
  }

  /* ── Day sections ── */
  .day-section {
    margin-bottom: var(--space-xl, 32px);
  }

  .day-header {
    display: flex;
    align-items: center;
    gap: var(--space-sm, 8px);
    margin-bottom: var(--space-md, 16px);
  }

  .day-label {
    font-family: var(--font-sans, sans-serif);
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--color-text-tertiary, #9a8e7c);
  }

  .day-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 18px;
    padding: 0 5px;
    background: var(--color-bg-tray, #f3efe7);
    color: var(--color-text-muted, #b5aa96);
    font-size: 0.68rem;
    font-weight: 600;
    border-radius: 9px;
    letter-spacing: 0.02em;
  }

  /* ── Loading shimmer ── */
  .feed-loading {
    display: flex;
    flex-direction: column;
    gap: var(--space-md, 16px);
    margin-top: var(--space-md, 16px);
  }

  .loading-shimmer {
    height: 80px;
    border-radius: var(--radius-lg, 14px);
    background: linear-gradient(
      90deg,
      var(--color-bg-tray, #f3efe7) 25%,
      var(--color-border, #e6e0d5) 50%,
      var(--color-bg-tray, #f3efe7) 75%
    );
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite;
  }

  .loading-shimmer--short {
    height: 80px;
    max-width: 75%;
  }

  @keyframes shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }

  /* ── State cards (empty, error) ── */
  .feed-state-card {
    margin-top: var(--space-xl, 32px);
    padding: var(--space-xl, 32px) var(--space-lg, 24px);
    background: var(--color-bg-surface, #fff);
    border: 1px solid var(--color-border, #e6e0d5);
    border-radius: var(--radius-lg, 14px);
    text-align: center;
  }

  .state-icon {
    display: block;
    font-size: 2rem;
    margin-bottom: var(--space-sm, 8px);
    opacity: 0.6;
  }

  .state-message {
    margin: 0 0 var(--space-xs, 4px);
    font-weight: 600;
    color: var(--color-text, #2c2419);
  }

  .feed-error-card {
    border-color: #fecaca;
    background: #fff5f5;
  }

  .feed-error-card .state-message {
    color: #b91c1c;
  }

  .state-hint {
    font-size: 0.875rem;
    color: var(--color-text-secondary, #5a5040);
    margin: var(--space-xs, 4px) 0 var(--space-md, 16px);
  }

  .cta-btn {
    display: inline-block;
    background: var(--color-text, #2c2419);
    color: var(--color-bg, #faf8f4);
    border: none;
    border-radius: var(--radius-md, 8px);
    padding: 0.55rem 1.25rem;
    cursor: pointer;
    font-size: 0.875rem;
    font-weight: 600;
    letter-spacing: 0.01em;
    transition: opacity 0.15s;
  }

  .cta-btn:hover {
    opacity: 0.85;
  }

  /* ── Pagination ── */
  .pager {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-md, 16px);
    margin-top: var(--space-xl, 32px);
    padding-top: var(--space-md, 16px);
    border-top: 1px solid var(--color-border, #e6e0d5);
  }

  .pager-btn {
    display: flex;
    align-items: center;
    gap: 5px;
    background: var(--color-bg-surface, #fff);
    color: var(--color-text, #2c2419);
    border: 1px solid var(--color-border, #e6e0d5);
    border-radius: var(--radius-md, 8px);
    padding: 0.45rem 1rem;
    cursor: pointer;
    font-size: 0.875rem;
    font-weight: 500;
    transition:
      background 0.15s,
      border-color 0.15s;
  }

  .pager-btn:hover:not(:disabled) {
    background: var(--color-bg-tray, #f3efe7);
    border-color: var(--color-text-muted, #b5aa96);
  }

  .pager-btn:disabled {
    opacity: 0.35;
    cursor: default;
  }

  .pager-arrow {
    font-size: 0.9em;
  }

  .pager-status {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 0.85rem;
    color: var(--color-text-secondary, #5a5040);
  }

  .pager-current {
    font-weight: 700;
    color: var(--color-text, #2c2419);
  }

  .pager-sep {
    color: var(--color-text-muted, #b5aa96);
  }

  .pager-total {
    color: var(--color-text-tertiary, #9a8e7c);
  }

  /* ── Mobile ── */
  @media (max-width: 768px) {
    .feed-page {
      padding: var(--space-md, 16px) var(--space-sm, 8px) var(--space-lg, 24px);
    }

    /* Shift title right to clear the fixed hamburger button (44px + 16px left) */
    .feed-header {
      padding-left: 60px;
    }

    .feed-title {
      font-size: 1.4rem;
    }

    .day-section {
      margin-bottom: var(--space-lg, 24px);
    }
  }
</style>
